/* Filter chips for the Judge.me review widget
   (sections/larcin-reviews-editorial.liquid).

   The chips never fetch, filter or sort anything themselves. Each one
   operates Judge.me's own control, so the results are always Judge.me's:
     - 5 stars / 4 stars: the rating in Judge.me's Filters panel
     - With photos: Judge.me's "Only Pictures" sort option
     - All ratings: closes the Filters panel (which clears its rating) and
       puts the sort back
   Judge.me's rating filter only holds while its Filters panel is open, so a
   chip opens the panel and the section keeps it out of sight
   (data-lrw-chip-panel); the chip already says what is filtered. Pressing
   Judge.me's Filters button then simply reveals the panel.
   The active chip is read back from Judge.me's own panel and sort, so it
   also follows the rating bars and the panel when they are used directly.
   Judge.me can re-render its widget; the chips are put back when it does. */
(() => {
  const PHOTOS = 'with_pictures';
  const CONTROLS = [
    '.jm-review-widget-sidebar-header__controls',
    '.jm-review-widget-minimal-header__filter-container',
    '.jm-review-widget-header__filters-wrapper',
  ].join(',');

  const nextFrame = () => new Promise((resolve) => requestAnimationFrame(() => resolve()));

  const setup = (root) => {
    if (root.dataset.lrwReady || root.dataset.lrwChips !== 'true') return;
    root.dataset.lrwReady = 'true';

    let defaultSort = '';
    let opening = false;

    const sortSelect = () => root.querySelector('.jm-review-widget .jdgm-sort-dropdown');
    const filterButton = () => root.querySelector('.jm-review-widget .jm-custom-answer-filter');
    const panel = () => root.querySelector('.jm-review-widget .jm-filter-panel');
    const rating = () => panel()?.querySelector('input[name="rating-filter"]:checked')?.value || '';
    const rowCount = (value) => {
      const row = [...root.querySelectorAll('.jm-histogram-row')].find(
        (item) => item.querySelector('.jm-histogram-row__rating')?.textContent.trim() === value
      );
      return row?.querySelector('.jm-histogram-row__frequency')?.textContent.trim();
    };

    const setSort = (value) => {
      const select = sortSelect();
      if (!select || !value || select.value === value) return;
      if (![...select.options].some((option) => option.value === value)) return;
      select.value = value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    };

    const closePanel = () => {
      if (panel()) filterButton()?.click();
    };

    const chooseRating = async (value) => {
      if (rating() === value) return;
      if (!panel()) {
        opening = true;
        root.dataset.lrwChipPanel = 'true';
        filterButton()?.click();
        for (let i = 0; i < 60 && !panel(); i += 1) await nextFrame();
        opening = false;
      }
      panel()?.querySelector(`input[name="rating-filter"][value="${value}"]`)?.click();
    };

    const current = () => {
      if (sortSelect()?.value === PHOTOS) return 'photos';
      return rating() || 'all';
    };

    const sync = () => {
      if (!panel() && !opening) delete root.dataset.lrwChipPanel;
      const active = current();
      root.querySelectorAll('.lrw-chip').forEach((chip) => {
        chip.setAttribute('aria-pressed', chip.dataset.filter === active ? 'true' : 'false');
      });
    };

    // Judge.me fills in its rating counts after the reviews, so the chips
    // are rebuilt whenever the set of chips worth offering changes.
    const build = () => {
      const controls = root.querySelector(CONTROLS);
      if (!controls || !root.querySelector('.jm-review-item')) return;
      if (!defaultSort) defaultSort = sortSelect()?.value || '';
      const hasBars = Boolean(root.querySelector('.jm-histogram-row'));

      const items = [
        ['all', root.dataset.lrwAll],
        ['5', root.dataset.lrwFive],
        ['4', root.dataset.lrwFour],
        ['photos', root.dataset.lrwPhotos],
      ].filter(([filter, label]) => {
        if (!label) return false;
        if (filter === '5' || filter === '4') {
          if (!filterButton()) return false;
          if (!hasBars) return true;
          const count = rowCount(filter);
          return Boolean(count) && count !== '0';
        }
        return filter !== 'photos' || Boolean(sortSelect());
      });

      const existing = controls.querySelector(':scope > .lrw-chips');
      const wanted = items.map(([filter]) => filter).join(' ');
      if (existing && existing.dataset.filters === wanted) return;
      existing?.remove();
      if (items.length < 2) return;

      const chips = document.createElement('div');
      chips.className = 'lrw-chips';
      chips.dataset.filters = wanted;
      chips.setAttribute('role', 'group');
      chips.setAttribute('aria-label', root.dataset.lrwLabel || 'Filter reviews');
      for (const [filter, label] of items) {
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'lrw-chip';
        chip.dataset.filter = filter;
        chip.textContent = label;
        chips.append(chip);
      }

      controls.prepend(chips);
      sync();
    };

    // Judge.me's Filters button while a chip holds the panel open: show the
    // panel rather than letting Judge.me close it and drop the rating.
    root.addEventListener(
      'click',
      (event) => {
        if (!event.isTrusted || !root.dataset.lrwChipPanel) return;
        if (!event.target.closest('.jm-custom-answer-filter')) return;
        event.stopPropagation();
        event.preventDefault();
        delete root.dataset.lrwChipPanel;
      },
      true
    );

    root.addEventListener('click', async (event) => {
      const chip = event.target.closest('.lrw-chip');
      if (!chip) return;
      const filter = chip.dataset.filter;
      if (filter === 'photos') {
        closePanel();
        setSort(PHOTOS);
      } else {
        if (sortSelect()?.value === PHOTOS) setSort(defaultSort);
        if (filter === 'all') closePanel();
        else await chooseRating(filter);
      }
      window.setTimeout(sync, 60);
    });

    root.addEventListener('change', () => window.setTimeout(sync, 0));

    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      window.setTimeout(() => {
        queued = false;
        build();
        sync();
      }, 50);
    }).observe(root, { childList: true, subtree: true, characterData: true });

    build();
  };

  const start = () => document.querySelectorAll('[data-lrw]').forEach(setup);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-lrw]').forEach(setup);
  });
})();
